import moment from 'moment';

import { ParsedBillData } from '../../../models';
import { BillImportMatchRequest, BillImportMatchResponse } from '../../../models/bill-import.models';
import { CommodityType } from '../../../../zentility/_shared/_zen-legacy-common/_models/commodity';
import { ZenRatesEnum } from '../../../../zentility/_shared/_enums/zen-rates.enum';
import { UnitDto } from '../../../../zentility/_shared/_model/unit-model';
import { resolveGasUnitId } from '../../../services/bill-persistence.service';
import { BillNavEntry, SaEditData } from '../state/bill-ai-flow.models';
import {
  FieldError,
  Severity,
  StepValidationResult,
  ValidationRule,
  ValidationContext,
  getByPath,
  isEmpty,
} from '../validation';

/**
 * Per-bill meter context the date-range / rate-bound / conditional rules need.
 * Resolved by the caller via {@link BillReviewValidationInput.meterContextFor};
 * each value is optional so a caller that can't resolve it falls back to the
 * safe defaults (rules that depend on a missing value simply don't fire).
 */
export interface BillMeterValidationContext {
  /** Meter commodity — selects the elec vs gas rate bounds. */
  commodityType?: CommodityType;
  /**
   * True when the bill is being entered in budgeting (Advanced) mode. Only
   * budgeting enforced the 59-day maximum period (the meter stepper did not).
   */
  isBudgetingMode?: boolean;
}

interface BillRuleContext extends ValidationContext {
  entity: ParsedBillData;
  isNew: boolean;  // whether the meter is new
}

/**
 * Resolve a meter's state + commodity from the match response/request,
 * honoring meter moves and preferring the user's SA edits for the state.
 *
 * Lives here (not the step component) so both the wizard step and the
 * BillAiFlowStore's fast-track analysis can build `meterContextFor` without a
 * store ↔ step circular import.
 */
export function resolveMeterContext(args: {
  meterGroupId: string;
  response: BillImportMatchResponse | null;
  request: BillImportMatchRequest | null;
  saEdits: Record<string, SaEditData>;
}): { state: string; commodityType: CommodityType } | null {
  const { meterGroupId, response, request, saEdits } = args;
  if (!response || !request) return null;
  for (const len of response.legalEntities) {
    for (const sa of len.serviceAddresses) {
      for (const meter of sa.meters) {
        if (meter.groupId !== meterGroupId) continue;
        const requestSa = request.legalEntities
          .find(l => l.groupId === len.groupId)?.serviceAddresses
          .find(s => s.groupId === sa.groupId);
        const editedState = saEdits[sa.groupId]?.state;
        const requestMeter = requestSa?.meters.find(m => m.groupId === meterGroupId);
        return {
          state: editedState || requestSa?.state || '',
          commodityType: requestMeter?.commodityType || CommodityType.Electricity,
        };
      }
    }
  }
  return null;
}

/**
 * Energy plan is required when a supplier is selected AND the meter is
 * existing.
 *
 * Rationale: for NEW meters, no energy plan exists yet in the database — the
 * backend will create one as part of the import. For EXISTING meters the
 * plan must be explicitly linked so the supply side of the bill attaches
 * to the right contract.
 */
const SUPPLIER_SELECTED_AND_EXISTING_METER = (ctx: BillRuleContext): boolean => {
  if (ctx.isNew) return false;
  const bill = ctx.entity;
  return bill?.supplierId != null && bill.supplierId !== '';
};

/** Mirrors the bill form's section defaults: with no explicit flags, a side is
 *  on only when it carries values (total-only bills open with both off). Covers
 *  store data validated before the form has saved its flags. */
function defaultSectionsOn(data: ParsedBillData | undefined): { supply: boolean; distribution: boolean } {
  const hasSupply = (data?.supplyCharges || 0) > 0 || (data?.supplyTaxes || 0) > 0
    || (data?.supplyTotalCost || 0) > 0;
  const hasDist = (data?.distributionCharges || 0) !== 0 || (data?.distributionTaxes || 0) !== 0
    || (data?.distributionTotalCost || 0) !== 0;
  const hasTotal = (data?.totalAmount ?? 0) !== 0;
  if (hasSupply || hasDist) return { supply: hasSupply, distribution: hasDist };
  const bothOn = !hasTotal;
  return { supply: bothOn, distribution: bothOn };
}

/** A dual bill half legitimately carries one side, so value-based inference is
 *  safe. On a consolidated bill a missing side is a data-entry error and must
 *  stay required (DEV-15235), so inference only applies to dual bills.
 *  An explicit isDualBilling (the user's Billing Method choice) wins over the
 *  supplier-document heuristic. Shared by the review step's radio, validation,
 *  and the import billType so they can never disagree. */
export function isDualHalf(data: ParsedBillData | undefined): boolean {
  if (data?.isDualBilling != null) return data.isDualBilling;
  return data?.billType === 'supplier';
}

/** The Supply section is off for this bill (a dual supplier bill carries supply,
 *  a dual utility bill does not). Explicit flags win; for a dual half without
 *  flags, infer the form's default so validation matches what the form will
 *  show; everything else defaults to on (both sides required). */
export function isSupplyDisabled(data: ParsedBillData | undefined): boolean {
  if (data?.supplyDisabled != null) return data.supplyDisabled;
  if (data?.financialsDisabled != null) return data.financialsDisabled;
  return isDualHalf(data) ? !defaultSectionsOn(data).supply : false;
}

/** The Distribution section is off for this bill. See {@link isSupplyDisabled}. */
export function isDistributionDisabled(data: ParsedBillData | undefined): boolean {
  if (data?.distributionDisabled != null) return data.distributionDisabled;
  if (data?.financialsDisabled != null) return data.financialsDisabled;
  return isDualHalf(data) ? !defaultSectionsOn(data).distribution : false;
}

/** A legacy bill may carry only a persisted section total (supply/distribution
 *  total) with no charge breakdown (null, or 0 — the older load mapping stored
 *  absent charges as 0) — that total satisfies the side's charges requirement
 *  so legacy bills stay saveable and keep their totals. Distribution may be a
 *  negative credit, so its callers accept a negative total as covering. */
function legacyTotalCoversCharges(
  charges: unknown,
  sectionTotal: number | null | undefined,
  allowNegativeTotal = false,
): boolean {
  const total = sectionTotal || 0;
  return (allowNegativeTotal ? total !== 0 : total > 0) && (isEmpty(charges) || charges === 0);
}

/** Supply charges are required unless the Supply section is off (dual utility
 *  bill, or a total-only bill where both sides are off) or a legacy supply
 *  total stands in for the breakdown. */
const SUPPLY_REQUIRED = (ctx: BillRuleContext): boolean =>
  !isSupplyDisabled(ctx.entity)
  && !legacyTotalCoversCharges(ctx.entity?.supplyCharges, ctx.entity?.supplyTotalCost);

/** Distribution charges are required unless the Distribution section is off
 *  (dual supplier bill, or a total-only bill) or a legacy distribution total
 *  stands in for the breakdown. */
const DISTRIBUTION_REQUIRED = (ctx: BillRuleContext): boolean =>
  !isDistributionDisabled(ctx.entity)
  && !legacyTotalCoversCharges(ctx.entity?.distributionCharges, ctx.entity?.distributionTotalCost, true);

/** A dual bill carrying supply but no supplier. A dual utility half with the
 *  Supply section off is exempt: its supplier arrives with the supplier bill. */
export function dualSupplyWithoutSupplier(data: ParsedBillData | undefined): boolean {
  return isDualHalf(data) && !isSupplyDisabled(data) && isEmpty(data?.supplierId);
}

export const BILL_REVIEW_RULES: ValidationRule[] = [
  { field: 'billingPeriodStart',  label: 'Billing Period Start', severity: 'required' },
  { field: 'billingPeriodEnd',    label: 'Billing Period End',   severity: 'required' },
  { field: 'usageAmount',         label: 'Usage Amount',         severity: 'required' },
  { field: 'totalAmount',         label: 'Total Amount',         severity: 'required' },
  // Supply lives on the supplier side, distribution on the utility side. A
  // supplier bill needs only supply; a dual utility bill only distribution; a
  // consolidated bill needs both. Total-only bills need neither.
  {
    field: 'supplyCharges', label: 'Supply Charges', severity: 'required',
    condition: (ctx) => SUPPLY_REQUIRED(ctx as BillRuleContext),
  },
  {
    field: 'distributionCharges', label: 'Distribution Charges', severity: 'required',
    condition: (ctx) => DISTRIBUTION_REQUIRED(ctx as BillRuleContext),
  },
  // Usage Unit is checked per commodity in usageUnitIssue below.
  {
    field: 'energyPlanId',
    label: 'Energy Plan',
    severity: 'required',
    condition: (ctx) => SUPPLIER_SELECTED_AND_EXISTING_METER(ctx as BillRuleContext),
  },
];

/** Present but 0 or negative warns, never blocks (DEV-15034). Distribution may be
 *  a credit (DEV-15235) and usage may be 0, so neither is listed. */
const NON_POSITIVE_WARN_FIELDS: Array<{ field: keyof ParsedBillData; label: string }> = [
  { field: 'supplyCharges',       label: 'Supply Charges' },
];

/** Minimum billing-period length both legacy dialogs enforced. */
export const MIN_BILL_PERIOD_DAYS = 15;
/** Maximum billing-period length the budget dialog enforced (budgeting mode only). */
export const MAX_BILL_PERIOD_DAYS = 59;

export interface BillReviewValidationInput {
  billData: Record<string, ParsedBillData>;
  billNavigation: BillNavEntry[];
  /** Bills left out of the import (skipped duplicates) — not validated. */
  excludeBillGroupIds?: ReadonlySet<string>;
  /** Wraps store.isNewEntity(); used to skip the energy-plan requirement on new meters. */
  isNewEntity: (groupId: string) => boolean;
  /**
   * Resolve the meter context (state / commodity / budgeting mode) for a bill.
   * Optional: when omitted, the date-range / rate-bound / conditional rules use
   * safe defaults and only the original required/positive rules run — preserving
   * the previous behavior for callers that don't supply it.
   */
  meterContextFor?: (nav: BillNavEntry) => BillMeterValidationContext | null | undefined;
  /**
   * Platform units (from UnitService). Used to express the derived-rate bounds
   * in the bill's selected unit (e.g. MWh) rather than the platform kWh. When
   * omitted, rate-bound messages fall back to platform bounds + a generic label.
   */
  units?: UnitDto[];
  /**
   * Severity of a dual bill with supply but no supplier. Blocks by default; the
   * post-import editor warns instead so existing supplier-less dual bills stay
   * editable.
   */
  dualSupplierSeverity?: Severity;
}

function describeBill(nav: BillNavEntry, data: ParsedBillData | undefined): string {
  const start = data?.billingPeriodStart;
  const end = data?.billingPeriodEnd;
  if (start && end) return `Bill ${start} – ${end}`;
  if (nav.meterUan) return `Bill for meter ${nav.meterUan}`;
  return `Bill ${nav.billGroupId.slice(0, 8)}`;
}

/** Commodity-aware supply-rate bounds (supplyCharges / usage). */
function supplyRateBounds(commodity?: CommodityType): { min: number; max: number } {
  return commodity === CommodityType.Gas
    ? { min: ZenRatesEnum.NAT_GAS_MIN_RATE, max: ZenRatesEnum.NAT_GAS_MAX_RATE }
    : { min: ZenRatesEnum.ELECTRICITY_MIN_RATE, max: ZenRatesEnum.ELECTRICITY_MAX_RATE };
}

/** Commodity-aware total-rate bounds (totalAmount / usage). */
function totalRateBounds(commodity?: CommodityType): { min: number; max: number } {
  return commodity === CommodityType.Gas
    ? { min: ZenRatesEnum.NAT_GAS_MIN_RATE, max: ZenRatesEnum.NAT_GAS_MAX_RATE }
    : { min: ZenRatesEnum.ELECTRICITY_MIN_TOTAL_RATE, max: ZenRatesEnum.ELECTRICITY_MAX_TOTAL_RATE };
}

/** Money format matching ZenNumberFormat.ELECTRIC_FEE_IN_DOLLARS ('1.2-5'). */
function formatMoney(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

/**
 * Express platform-unit rate bounds in the bill's selected unit and return its
 * label. ZenRatesEnum bounds are per platform unit (electricity → kWh); the
 * unit's toPlatformMultiplier scales them (e.g. MWh ×1000 → 10–800). Falls back
 * to the platform bounds and a generic "unit" label when the unit is unknown.
 */
function boundsInUnit(
  bounds: { min: number; max: number },
  unit?: UnitDto,
): { min: number; max: number; label: string } {
  const mult = unit?.toPlatformMultiplier ?? 1;
  return { min: bounds.min * mult, max: bounds.max * mult, label: unit?.unit ?? 'unit' };
}

/** Parse an ISO/date-like string into a valid moment, or null. */
function toValidMoment(value: unknown): moment.Moment | null {
  if (value == null || value === '') return null;
  const m = moment(value as any);
  return m.isValid() ? m : null;
}

/**
 * Date-range constraints restored from the legacy dialogs:
 *  - end date must NOT be in the future,
 *  - at least {@link MIN_BILL_PERIOD_DAYS} between start and end,
 *  - at most {@link MAX_BILL_PERIOD_DAYS} between start and end (budgeting only).
 * Only runs when both dates are present and valid (the "required" rules already
 * cover the missing-date case).
 */
function dateRangeErrors(
  data: ParsedBillData,
  ctx: BillMeterValidationContext,
  entityId: string,
  entityLabel: string,
): FieldError[] {
  const errors: FieldError[] = [];
  const start = toValidMoment(data.billingPeriodStart);
  const end = toValidMoment(data.billingPeriodEnd);
  if (!start || !end) return errors;

  // End date cannot be later than today.
  if (end.isAfter(moment(), 'day')) {
    errors.push({
      entityId, entityLabel, field: 'billingPeriodEnd', label: 'Billing Period End',
      severity: 'required',
      summaryMessage: 'Billing Period End cannot be in the future',
    });
  }

  const dateDiff = end.diff(start, 'days');
  if (dateDiff < MIN_BILL_PERIOD_DAYS) {
    errors.push({
      entityId, entityLabel, field: 'billingPeriodStart', label: 'Billing Period',
      severity: 'required',
      summaryMessage: `Billing period must be at least ${MIN_BILL_PERIOD_DAYS} days`,
    });
  } else if (ctx.isBudgetingMode && dateDiff > MAX_BILL_PERIOD_DAYS) {
    errors.push({
      entityId, entityLabel, field: 'billingPeriodStart', label: 'Billing Period',
      severity: 'required',
      summaryMessage: `Billing period cannot exceed ${MAX_BILL_PERIOD_DAYS} days`,
    });
  }

  return errors;
}

/**
 * Derived-rate bounds restored from the budget dialog. Supply rate
 * (supplyCharges / usage) and total rate (totalAmount / usage) must each fall
 * within commodity-aware bounds. Only runs when usage > 0 and the charge is a
 * real positive number (the positive-number rules cover the rest).
 */
function rateBoundErrors(
  data: ParsedBillData,
  ctx: BillMeterValidationContext,
  entityId: string,
  entityLabel: string,
  units?: UnitDto[],
): FieldError[] {
  const errors: FieldError[] = [];
  const usage = data.usageAmount;
  if (typeof usage !== 'number' || !(usage > 0)) return errors;

  // Rate = charge / usage, so it's expressed in the bill's selected unit. Scale
  // the platform bounds and label the message with that unit (e.g. /MWh).
  const unit = units?.find(u => u.unit === data.usageUnit);

  const supply = data.supplyCharges;
  if (typeof supply === 'number' && supply > 0 && !isSupplyDisabled(data)) {
    const rate = supply / usage;
    const { min, max, label } = boundsInUnit(supplyRateBounds(ctx.commodityType), unit);
    if (rate < min || rate > max) {
      errors.push({
        entityId, entityLabel, field: 'supplyCharges', label: 'Supply Charges',
        severity: 'warn',
        summaryMessage: `Supply rate ($${formatMoney(rate)}/${label}) should be between $${formatMoney(min)} and $${formatMoney(max)}`,
      });
    }
  }

  const total = data.totalAmount;
  if (typeof total === 'number' && total > 0) {
    const rate = total / usage;
    const { min, max, label } = boundsInUnit(totalRateBounds(ctx.commodityType), unit);
    if (rate < min || rate > max) {
      errors.push({
        entityId, entityLabel, field: 'totalAmount', label: 'Total Amount',
        severity: 'warn',
        summaryMessage: `Total rate ($${formatMoney(rate)}/${label}) should be between $${formatMoney(min)} and $${formatMoney(max)}`,
      });
    }
  }

  return errors;
}

/**
 * Usage Unit, by commodity. Electricity falls back to kWh server-side, so a missing
 * unit only warns. Gas has no safe fallback (therms stored as Dth is a 10x error) and
 * the API rejects a gas bill without one ("Unit is required"), so on gas the unit must
 * resolve to a platform gas unit — a blank, or a printed unit the dropdown doesn't
 * list ("CCF (100 cu ft)"), blocks. The bill's own commodity or its meter's marks it
 * gas; either is enough, so a gas bill still on an unresolved meter is caught.
 */
function usageUnitIssue(
  data: ParsedBillData,
  meterCommodity: CommodityType | undefined,
  entityId: string,
  entityLabel: string,
): FieldError | null {
  const base = { entityId, entityLabel, field: 'usageUnit', label: 'Usage Unit' };
  const isGas = data.commodityType === CommodityType.Gas || meterCommodity === CommodityType.Gas;
  if (!isGas) {
    return isEmpty(data.usageUnit) ? { ...base, severity: 'warn' } : null;
  }
  if (resolveGasUnitId(data.usageUnitId, data.usageUnit) !== undefined) return null;
  const printed = data.usageUnit?.trim();
  return {
    ...base,
    severity: 'required',
    // Blank reads as the default "Usage Unit is required"
    summaryMessage: printed ? `Usage Unit "${printed}" is not a gas unit, pick one from the list` : undefined,
  };
}

export function validateBillReviewStep(input: BillReviewValidationInput): StepValidationResult {
  const errors: FieldError[] = [];
  const warnings: FieldError[] = [];

  for (const nav of input.billNavigation) {
    if (input.excludeBillGroupIds?.has(nav.billGroupId)) continue;
    const data = input.billData[nav.billGroupId];
    if (!data) continue;
    const entityLabel = describeBill(nav, data);
    const isNew = nav.meterGroupId ? input.isNewEntity(nav.meterGroupId) : false;
    // The date-range / rate-bound / conditional rules only run when the caller
    // supplies a meter-context resolver. Callers that don't (legacy callers and
    // pure required-field checks) keep the original behavior.
    const meterCtx = input.meterContextFor ? (input.meterContextFor(nav) ?? {}) : null;

    for (const rule of BILL_REVIEW_RULES) {
      if (rule.condition && !rule.condition({ entity: data, isNew })) continue;
      const value = getByPath(data, rule.field);
      if (isEmpty(value)) {
        const err: FieldError = {
          entityId: nav.billGroupId,
          entityLabel,
          field: rule.field,
          label: rule.label,
          severity: rule.severity,
        };
        if (rule.severity === 'required') errors.push(err);
        else warnings.push(err);
      }
    }

    const unitIssue = usageUnitIssue(data, meterCtx?.commodityType, nav.billGroupId, entityLabel);
    if (unitIssue?.severity === 'required') errors.push(unitIssue);
    else if (unitIssue) warnings.push(unitIssue);

    // Dual billing means a supplier sells the supply, so it can't be "On Utility".
    if (dualSupplyWithoutSupplier(data)) {
      const severity = input.dualSupplierSeverity ?? 'required';
      (severity === 'required' ? errors : warnings).push({
        entityId: nav.billGroupId,
        entityLabel,
        field: 'supplierId',
        label: 'Supplier',
        severity,
        summaryMessage: 'Dual bills need a supplier (not On Utility)',
      });
    }

    // Skipped on a bill with no total: the editor seeds a new bill with $0 everywhere.
    const hasTotal = typeof data.totalAmount === 'number' && data.totalAmount !== 0;
    for (const { field, label } of NON_POSITIVE_WARN_FIELDS) {
      if (!hasTotal) break;
      // Skip a side that's off for this bill or covered by a legacy section total.
      if (field === 'supplyCharges'
        && (isSupplyDisabled(data) || legacyTotalCoversCharges(data.supplyCharges, data.supplyTotalCost))) continue;
      const value = (data as any)[field];
      if (typeof value === 'number' && !Number.isNaN(value) && value <= 0) {
        warnings.push({
          entityId: nav.billGroupId,
          entityLabel,
          field: field as string,
          label,
          severity: 'warn',
          summaryMessage: `${label} is ${value < 0 ? 'negative' : '0'}, check the bill`,
        });
      }
    }

    // A negative total is a credit (DEV-15235) and $0.00 warns (DEV-15034). The
    // period gate keeps a freshly opened Add Bill form (seeded $0, no dates) quiet.
    const totalValue = data.totalAmount;
    const hasPeriod = !isEmpty(data.billingPeriodStart) || !isEmpty(data.billingPeriodEnd);
    if (!isEmpty(totalValue) && typeof totalValue !== 'number') {
      errors.push({
        entityId: nav.billGroupId,
        entityLabel,
        field: 'totalAmount',
        label: 'Total Amount',
        severity: 'required',
        summaryMessage: 'Total Amount must be a number',
      });
    } else if (hasPeriod && totalValue === 0) {
      warnings.push({
        entityId: nav.billGroupId,
        entityLabel,
        field: 'totalAmount',
        label: 'Total Amount',
        severity: 'warn',
        summaryMessage: 'Total Amount is $0.00, check the bill',
      });
    }

    // Date-range, derived-rate-bound, and conditional-field rules restored from
    // the legacy add-bill dialogs. These depend on the per-bill meter context.
    if (meterCtx) {
      errors.push(...dateRangeErrors(data, meterCtx, nav.billGroupId, entityLabel));
      // Rate bounds derive from the supply/distribution charges — skip for
      // total-only bills, which have no breakdown to rate-check. Routed by
      // severity so warn-level bounds inform without blocking the step.
      if (!data.financialsDisabled) {
        for (const issue of rateBoundErrors(data, meterCtx, nav.billGroupId, entityLabel, input.units)) {
          if (issue.severity === 'required') errors.push(issue);
          else warnings.push(issue);
        }
      }
    }
  }

  return { errors, warnings, hasBlockers: errors.length > 0 };
}
